/**
 * ObsidianWeb — Canvas viewer + editor (Task 7 + Task 14).
 *
 * React renderer for parsed JSON Canvas documents. Draws nodes
 * (text / file / link / group) and edges as a single SVG element with a
 * `<g transform="translate(...) scale(...)">` viewport so pan and zoom are
 * one matrix update — no per-node DOM churn on interaction.
 *
 * Interaction (always-on):
 *   - mouse-drag on empty space  → pan (translate the viewport group)
 *   - wheel                      → zoom around the cursor, clamped to
 *                                  [MIN_SCALE, MAX_SCALE]
 *
 * Interaction (editor mode — `editable` prop):
 *   - left-click on a node              → select
 *   - drag a selected node              → move (world-space, snapped to int)
 *   - double-click a `text` or `group`
 *     node                              → inline edit (text content / label)
 *   - Delete / Backspace with selection → remove the node and any edges
 *                                          that referenced it (no orphans),
 *                                          or remove the selected edge
 *   - "+ Text" button                    → add a text node at the viewport
 *                                          centre and open its inline editor
 *   - "Connect" button (node selected)   → next node click adds an edge
 *                                          from the selection to that node
 *   - click an edge                      → select it (Delete removes it)
 *   - "Save" button (header)            → calls `onSave(canvas)` with the
 *                                          current document; the parent
 *                                          owns the optimistic-lock pair
 *                                          and the HTTP round trip.
 *
 * The component's *internal* state holds only the viewport `Transform`,
 * the current selection, and the in-flight text-edit buffer. The canvas
 * document itself is owned by the parent — every mutation is reported
 * through `onChange(canvas)` so the parent can drive validation and the
 * save button's enabled state. This keeps the editor a pure controlled
 * component (no internal forking of canvas state) which is essential for
 * the round-trip contract: what the parent sees is what would be saved.
 *
 * The pure helpers (`computeBounds`, `applyZoom`, `applyPan`, `edgePath`,
 * `colorToken`, `renderCanvasToSvgString`, `moveNode`, `updateNodeBody`,
 * `deleteNode`) are exported so the unit tests (which run under vitest's
 * `node` environment — no DOM) can verify behaviour without React.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type WheelEvent as ReactWheelEvent } from 'react';

/* ── Local structural types ───────────────────────────────────────────── *
 * Kept in-file rather than imported from `@obsidianweb/vault-parsers` so
 * the viewer is independently testable and the package's `exports` map
 * (which only publishes `./dist/index.js`) does not need to grow a new
 * subpath for this task. Shapes are kept structurally compatible with the
 * canonical types in `packages/vault-parsers/src/canvas/schema.ts`. */

export type CanvasSide = 'top' | 'right' | 'bottom' | 'left';
export type CanvasNodeType = 'text' | 'file' | 'link' | 'group';

interface NodeBase {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly color?: string;
}
export interface TextNode extends NodeBase {
  readonly type: 'text';
  readonly text: string;
}
export interface FileNode extends NodeBase {
  readonly type: 'file';
  readonly file: string;
  readonly subpath?: string;
}
export interface LinkNode extends NodeBase {
  readonly type: 'link';
  readonly url: string;
}
export interface GroupNode extends NodeBase {
  readonly type: 'group';
  readonly label?: string;
}
export type CanvasNode = TextNode | FileNode | LinkNode | GroupNode;

export interface CanvasEdge {
  readonly id: string;
  readonly fromNode: string;
  readonly toNode: string;
  readonly fromSide?: CanvasSide;
  readonly toSide?: CanvasSide;
  readonly fromEnd?: 'none' | 'arrow';
  readonly toEnd?: 'none' | 'arrow';
  readonly color?: string;
  readonly label?: string;
}

export interface Canvas {
  readonly nodes: readonly CanvasNode[];
  readonly edges: readonly CanvasEdge[];
}

/* ── Viewport transform helpers (pure, exported for tests) ────────────── */

export interface Transform {
  /** World-units → screen-units. */
  readonly scale: number;
  /** Screen-space translation applied after scaling. */
  readonly tx: number;
  readonly ty: number;
}

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 4;
const ZOOM_STEP = 1.0015;

/** Compute the world-space bounding box for a canvas. Returns a unit box
 *  centred at origin if the canvas is empty (so the viewer still has a
 *  finite viewBox to render into). */
export function computeBounds(canvas: Canvas): {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly width: number;
  readonly height: number;
} {
  if (canvas.nodes.length === 0) {
    return { minX: -50, minY: -50, maxX: 50, maxY: 50, width: 100, height: 100 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of canvas.nodes) {
    if (n.x < minX) minX = n.x;
    if (n.y < minY) minY = n.y;
    if (n.x + n.width > maxX) maxX = n.x + n.width;
    if (n.y + n.height > maxY) maxY = n.y + n.height;
  }
  // Padding so labels at the edge aren't clipped.
  const pad = 64;
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/** Compute the initial viewport transform that fits the canvas into the
 *  given screen-space size with a single uniform scale. */
export function fitTransform(canvas: Canvas, viewW: number, viewH: number): Transform {
  const b = computeBounds(canvas);
  if (b.width === 0 || b.height === 0 || viewW === 0 || viewH === 0) {
    return { scale: 1, tx: 0, ty: 0 };
  }
  const scale = Math.min(viewW / b.width, viewH / b.height, MAX_SCALE);
  const clamped = Math.max(MIN_SCALE, scale);
  // Centre the bounds inside the viewport.
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  return {
    scale: clamped,
    tx: viewW / 2 - cx * clamped,
    ty: viewH / 2 - cy * clamped,
  };
}

/** Translate the viewport by a screen-space delta (drag). */
export function applyPan(t: Transform, dxScreen: number, dyScreen: number): Transform {
  return { scale: t.scale, tx: t.tx + dxScreen, ty: t.ty + dyScreen };
}

/**
 * Zoom the viewport around an anchor point in screen coordinates. The
 * world-space point under the cursor stays put — standard "zoom to
 * cursor" behaviour. `wheelDelta` is the raw `WheelEvent.deltaY` value
 * (positive = zoom out).
 */
export function applyZoom(
  t: Transform,
  wheelDelta: number,
  anchorX: number,
  anchorY: number,
): Transform {
  const factor = Math.pow(ZOOM_STEP, -wheelDelta);
  const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, t.scale * factor));
  if (next === t.scale) return t;
  // anchor in world space: (anchorX - tx) / scale
  const wx = (anchorX - t.tx) / t.scale;
  const wy = (anchorY - t.ty) / t.scale;
  return {
    scale: next,
    tx: anchorX - wx * next,
    ty: anchorY - wy * next,
  };
}

/* ── Edge geometry (pure) ─────────────────────────────────────────────── */

interface Point {
  readonly x: number;
  readonly y: number;
}

/** Return the world-space coordinate of a node's side anchor. If `side` is
 *  undefined we fall back to the geometric centre, matching Obsidian's
 *  behaviour for edges that don't pin a side. */
export function edgeAnchor(node: CanvasNode, side: CanvasSide | undefined): Point {
  const cx = node.x + node.width / 2;
  const cy = node.y + node.height / 2;
  switch (side) {
    case 'top':
      return { x: cx, y: node.y };
    case 'bottom':
      return { x: cx, y: node.y + node.height };
    case 'left':
      return { x: node.x, y: cy };
    case 'right':
      return { x: node.x + node.width, y: cy };
    default:
      return { x: cx, y: cy };
  }
}

/** Best-guess side when an edge doesn't specify one — pick whichever of
 *  the four sides is closest to the opposing node's centre. Mirrors
 *  Obsidian's auto-routing for the common "no explicit side" case. */
export function inferSide(from: CanvasNode, to: CanvasNode): CanvasSide {
  const fromCx = from.x + from.width / 2;
  const fromCy = from.y + from.height / 2;
  const toCx = to.x + to.width / 2;
  const toCy = to.y + to.height / 2;
  const dx = toCx - fromCx;
  const dy = toCy - fromCy;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
  return dy >= 0 ? 'bottom' : 'top';
}

/** Cubic-Bezier SVG path between two side anchors. Control points are
 *  pulled along the side normal so edges leave/enter perpendicular —
 *  visually consistent with Obsidian's renderer. */
export function edgePath(
  from: CanvasNode,
  to: CanvasNode,
  fromSide: CanvasSide | undefined,
  toSide: CanvasSide | undefined,
): string {
  const fs = fromSide ?? inferSide(from, to);
  const ts = toSide ?? inferSide(to, from);
  const a = edgeAnchor(from, fs);
  const b = edgeAnchor(to, ts);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.max(40, Math.hypot(dx, dy));
  const reach = Math.min(160, dist / 2);
  const normal = (s: CanvasSide): Point => {
    switch (s) {
      case 'top':
        return { x: 0, y: -1 };
      case 'bottom':
        return { x: 0, y: 1 };
      case 'left':
        return { x: -1, y: 0 };
      case 'right':
        return { x: 1, y: 0 };
      default: {
        const _exhaustive: never = s;
        return _exhaustive;
      }
    }
  };
  const na = normal(fs);
  const nb = normal(ts);
  const c1 = { x: a.x + na.x * reach, y: a.y + na.y * reach };
  const c2 = { x: b.x + nb.x * reach, y: b.y + nb.y * reach };
  return `M ${a.x} ${a.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${b.x} ${b.y}`;
}

/* ── Colour palette ───────────────────────────────────────────────────── */

/**
 * JSON Canvas colours are either a CSS hex string (`#rrggbb`) or a single
 * digit `1`–`6` mapping to a fixed palette (red / orange / yellow / green
 * / cyan / purple). Returns a CSS-compatible colour or `undefined` if the
 * input is unset.
 */
export function colorToken(c: string | undefined): string | undefined {
  if (c === undefined) return undefined;
  const palette: Readonly<Record<string, string>> = {
    '1': '#e93147', // red
    '2': '#ec7500', // orange
    '3': '#e0ac00', // yellow
    '4': '#08b94e', // green
    '5': '#00bfbc', // cyan
    '6': '#7852ee', // purple
  };
  return palette[c] ?? c;
}

/* ── SVG primitive renderers (used by component + string renderer) ──── */

interface NodeStyle {
  readonly fill: string;
  readonly stroke: string;
  readonly text: string;
}

function styleForNode(n: CanvasNode): NodeStyle {
  const accent = colorToken(n.color);
  const stroke = accent ?? 'var(--background-modifier-border, #3f3f46)';
  if (n.type === 'group') {
    return {
      fill: accent ? `${accent}1a` : 'rgba(255,255,255,0.03)',
      stroke,
      text: 'var(--text-muted, #a1a1aa)',
    };
  }
  return {
    fill: 'var(--background-primary, #1e1e1e)',
    stroke,
    text: 'var(--text-normal, #dcddde)',
  };
}

/** Wrap a long text node body into rendered <tspan> lines for SVG. */
export function wrapNodeText(
  text: string,
  width: number,
  fontSize: number,
  maxLines: number,
): readonly string[] {
  const avgChar = fontSize * 0.55;
  const charsPerLine = Math.max(1, Math.floor((width - 16) / avgChar));
  const out: string[] = [];
  for (const para of text.split(/\r?\n/)) {
    if (para === '') {
      out.push('');
      continue;
    }
    let remaining = para;
    while (remaining.length > charsPerLine) {
      // Try to break at the last space within the window.
      const slice = remaining.slice(0, charsPerLine + 1);
      const breakAt = slice.lastIndexOf(' ');
      const cut = breakAt > Math.floor(charsPerLine / 2) ? breakAt : charsPerLine;
      out.push(remaining.slice(0, cut));
      remaining = remaining.slice(cut).replace(/^\s+/, '');
    }
    out.push(remaining);
  }
  if (out.length > maxLines) {
    const truncated = out.slice(0, maxLines);
    const last = truncated[truncated.length - 1] ?? '';
    truncated[truncated.length - 1] = (last.length > 1 ? last.slice(0, -1) : last) + '…';
    return truncated;
  }
  return out;
}

/* ── Pure edit-mode mutations (exported for tests) ────────────────────── */

/**
 * Move a node by a world-space delta. Returns a new `Canvas` with one
 * node's `x`/`y` updated; all other nodes and edges share-reference the
 * original. No-op (returns the input) if the node id is unknown.
 *
 * Coordinates are rounded to integers — JSON Canvas allows floats but
 * Obsidian's writer emits integers and our optimistic-lock check is on
 * the serialised bytes, so sub-pixel coordinates churn the hash without
 * any visible benefit.
 */
export function moveNode(canvas: Canvas, id: string, dx: number, dy: number): Canvas {
  let touched = false;
  const nodes = canvas.nodes.map((n) => {
    if (n.id !== id) return n;
    touched = true;
    return { ...n, x: Math.round(n.x + dx), y: Math.round(n.y + dy) };
  });
  return touched ? { ...canvas, nodes } : canvas;
}

/**
 * Set the absolute position of a node. Used by the drag handler, which
 * tracks the cumulative drag in world space and re-applies it to the
 * node's *original* position on each frame (so cancelling the drag
 * mid-flight returns to the start, not to a halfway state).
 */
export function setNodePosition(canvas: Canvas, id: string, x: number, y: number): Canvas {
  let touched = false;
  const nodes = canvas.nodes.map((n) => {
    if (n.id !== id) return n;
    touched = true;
    return { ...n, x: Math.round(x), y: Math.round(y) };
  });
  return touched ? { ...canvas, nodes } : canvas;
}

/**
 * Update the human-visible body of a node (text content for `text`,
 * label for `group`, file path for `file`, url for `link`). Other node
 * types are left untouched. Empty values are rejected (returns the input
 * unchanged) since the schema requires non-empty `text`/`file`/`url`.
 */
export function updateNodeBody(canvas: Canvas, id: string, body: string): Canvas {
  let touched = false;
  const nodes = canvas.nodes.map((n) => {
    if (n.id !== id) return n;
    switch (n.type) {
      case 'text': {
        touched = true;
        return { ...n, text: body };
      }
      case 'group': {
        touched = true;
        // An empty group label is valid (the field is optional) — clear
        // by omission rather than storing an empty string.
        if (body === '') {
          // Rebuild without the label key. exactOptionalPropertyTypes
          // means `{ ...n, label: undefined }` would fail typecheck, so
          // we construct the new object field-by-field.
          const cleared: GroupNode = {
            id: n.id,
            type: 'group',
            x: n.x,
            y: n.y,
            width: n.width,
            height: n.height,
            ...(n.color !== undefined ? { color: n.color } : {}),
          };
          return cleared;
        }
        return { ...n, label: body };
      }
      case 'file': {
        if (body.length === 0) return n;
        touched = true;
        return { ...n, file: body };
      }
      case 'link': {
        if (body.length === 0) return n;
        touched = true;
        return { ...n, url: body };
      }
      default: {
        const _exhaustive: never = n;
        return _exhaustive;
      }
    }
  });
  return touched ? { ...canvas, nodes } : canvas;
}

/**
 * Delete a node and every edge that referenced it. The schema rejects
 * dangling edges, so the editor MUST drop them in the same operation —
 * otherwise the next save would 409 with `unknown_node_reference`.
 */
export function deleteNode(canvas: Canvas, id: string): Canvas {
  if (!canvas.nodes.some((n) => n.id === id)) return canvas;
  const nodes = canvas.nodes.filter((n) => n.id !== id);
  const edges = canvas.edges.filter((e) => e.fromNode !== id && e.toNode !== id);
  return { ...canvas, nodes, edges };
}

/**
 * Generate a canvas element id in Obsidian's format (16 lowercase hex
 * chars), guaranteed not to collide with any id already in the document.
 */
export function newCanvasId(canvas: Canvas): string {
  const taken = new Set<string>([
    ...canvas.nodes.map((n) => n.id),
    ...canvas.edges.map((e) => e.id),
  ]);
  for (;;) {
    let id = '';
    for (let i = 0; i < 16; i++) {
      id += Math.floor(Math.random() * 16).toString(16);
    }
    if (!taken.has(id)) return id;
  }
}

/** Default geometry for a freshly-added text node (matches Obsidian's
 *  "Add card" default card size). */
export const NEW_TEXT_NODE_WIDTH = 250;
export const NEW_TEXT_NODE_HEIGHT = 60;

/**
 * Add a new `text` node centred on the given world-space point (FR-4.3
 * "add text nodes"). Returns the new document plus the generated node id
 * so the caller can select it / open the inline editor.
 */
export function addTextNode(
  canvas: Canvas,
  worldX: number,
  worldY: number,
  text = 'New note',
): { readonly canvas: Canvas; readonly id: string } {
  const id = newCanvasId(canvas);
  const node: TextNode = {
    id,
    type: 'text',
    x: Math.round(worldX - NEW_TEXT_NODE_WIDTH / 2),
    y: Math.round(worldY - NEW_TEXT_NODE_HEIGHT / 2),
    width: NEW_TEXT_NODE_WIDTH,
    height: NEW_TEXT_NODE_HEIGHT,
    text,
  };
  return { canvas: { ...canvas, nodes: [...canvas.nodes, node] }, id };
}

/**
 * Add an edge between two existing nodes (FR-4.3 "add edges"). Returns
 * the input unchanged when either endpoint is unknown or when the exact
 * same connection would be a self-loop — the schema would reject a
 * dangling reference on save, so the editor never creates one.
 */
export function addEdge(
  canvas: Canvas,
  fromNode: string,
  toNode: string,
): { readonly canvas: Canvas; readonly id: string | null } {
  if (fromNode === toNode) return { canvas, id: null };
  const has = (id: string): boolean => canvas.nodes.some((n) => n.id === id);
  if (!has(fromNode) || !has(toNode)) return { canvas, id: null };
  const id = newCanvasId(canvas);
  const edge: CanvasEdge = { id, fromNode, toNode, toEnd: 'arrow' };
  return { canvas: { ...canvas, edges: [...canvas.edges, edge] }, id };
}

/** Remove an edge by id (FR-4.3 "remove edges"). No-op on unknown ids. */
export function deleteEdge(canvas: Canvas, id: string): Canvas {
  if (!canvas.edges.some((e) => e.id === id)) return canvas;
  return { ...canvas, edges: canvas.edges.filter((e) => e.id !== id) };
}

/* ── Pure SVG string renderer (used by tests + SSR) ───────────────────── */

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function nodeBody(n: CanvasNode): string {
  switch (n.type) {
    case 'text':
      return n.text;
    case 'file':
      return n.subpath ? `📄 ${n.file}${n.subpath}` : `📄 ${n.file}`;
    case 'link':
      return `🔗 ${n.url}`;
    case 'group':
      return n.label ?? '';
    default: {
      const _exhaustive: never = n;
      return _exhaustive;
    }
  }
}

function renderNodeSvg(n: CanvasNode): string {
  const s = styleForNode(n);
  const body = nodeBody(n);
  const fontSize = n.type === 'group' ? 13 : 14;
  const lines = wrapNodeText(body, n.width, fontSize, Math.max(1, Math.floor(n.height / 18)));
  const tspans = lines
    .map(
      (line, i) =>
        `<tspan x="${n.x + 12}" dy="${i === 0 ? fontSize + 4 : fontSize + 2}">${esc(line)}</tspan>`,
    )
    .join('');
  const rx = n.type === 'group' ? 4 : 8;
  return (
    `<g class="ow-canvas-node ow-canvas-node-${n.type}" data-node-id="${esc(n.id)}">` +
    `<rect x="${n.x}" y="${n.y}" width="${n.width}" height="${n.height}" rx="${rx}" ry="${rx}"` +
    ` fill="${s.fill}" stroke="${s.stroke}" stroke-width="${n.type === 'group' ? 1 : 1.5}" />` +
    `<text x="${n.x + 12}" y="${n.y}" fill="${s.text}" font-size="${fontSize}"` +
    ` font-family="var(--font-interface, system-ui, sans-serif)">${tspans}</text>` +
    `</g>`
  );
}

function renderEdgeSvg(
  e: CanvasEdge,
  from: CanvasNode,
  to: CanvasNode,
  markerId: string,
): string {
  const d = edgePath(from, to, e.fromSide, e.toSide);
  const stroke = colorToken(e.color) ?? 'var(--background-modifier-border-hover, #6b7280)';
  const toEnd = e.toEnd ?? 'arrow';
  const fromEnd = e.fromEnd ?? 'none';
  const markerEnd = toEnd === 'arrow' ? ` marker-end="url(#${markerId})"` : '';
  const markerStart = fromEnd === 'arrow' ? ` marker-start="url(#${markerId})"` : '';
  const labelSvg = e.label
    ? (() => {
        const a = edgeAnchor(from, e.fromSide ?? inferSide(from, to));
        const b = edgeAnchor(to, e.toSide ?? inferSide(to, from));
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        return (
          `<text x="${mx}" y="${my}" text-anchor="middle" font-size="12"` +
          ` fill="var(--text-muted, #a1a1aa)" font-family="var(--font-interface, system-ui, sans-serif)"` +
          ` paint-order="stroke" stroke="var(--background-primary, #1e1e1e)" stroke-width="3">` +
          `${esc(e.label)}</text>`
        );
      })()
    : '';
  return (
    `<g class="ow-canvas-edge" data-edge-id="${esc(e.id)}">` +
    `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="2"${markerEnd}${markerStart} />` +
    labelSvg +
    `</g>`
  );
}

/**
 * Render the canvas to a self-contained SVG string. Used by SSR previews
 * and by the unit tests (which run in vitest's node environment without a
 * real DOM). Groups are rendered first so they sit behind nodes.
 */
export function renderCanvasToSvgString(
  canvas: Canvas,
  options: { readonly width?: number; readonly height?: number } = {},
): string {
  const b = computeBounds(canvas);
  const w = options.width ?? Math.max(320, Math.round(b.width));
  const h = options.height ?? Math.max(240, Math.round(b.height));
  const nodeById = new Map(canvas.nodes.map((n) => [n.id, n] as const));
  const groups = canvas.nodes.filter((n) => n.type === 'group');
  const others = canvas.nodes.filter((n) => n.type !== 'group');
  const edges = canvas.edges
    .map((e) => {
      const from = nodeById.get(e.fromNode);
      const to = nodeById.get(e.toNode);
      if (!from || !to) return '';
      return renderEdgeSvg(e, from, to, 'ow-canvas-arrow');
    })
    .join('');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${b.minX} ${b.minY} ${b.width} ${b.height}"` +
    ` width="${w}" height="${h}" class="ow-canvas-svg" role="img" aria-label="Canvas">` +
    `<defs>` +
    `<marker id="ow-canvas-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6"` +
    ` markerHeight="6" orient="auto-start-reverse">` +
    `<path d="M0,0 L10,5 L0,10 z" fill="var(--background-modifier-border-hover, #6b7280)" />` +
    `</marker>` +
    `</defs>` +
    groups.map(renderNodeSvg).join('') +
    edges +
    others.map(renderNodeSvg).join('') +
    `</svg>`
  );
}

/* ── React component ──────────────────────────────────────────────────── */

export interface CanvasViewerProps {
  readonly canvas: Canvas;
  /** Initial viewport size in CSS pixels. Defaults to a sensible 800×600. */
  readonly width?: number;
  readonly height?: number;
  /** Optional click handler for `file` nodes (e.g. open in a new tab). */
  readonly onOpenFile?: (path: string, subpath: string | undefined) => void;
  /**
   * Switch the canvas into editor mode. When true:
   *   - left-click selects a node; drag-from-node moves it
   *   - double-click on text/group opens an inline editor
   *   - Delete/Backspace removes the selected node + dangling edges
   *   - a "Save" button is shown in the corner and fires `onSave`
   *
   * Defaults to false (preserves the read-only viewer behaviour shipped
   * in Task 7).
   */
  readonly editable?: boolean;
  /**
   * Called whenever the canvas is mutated in editor mode. The parent
   * owns the canvas state — the editor never holds a fork. Required
   * when `editable` is true.
   */
  readonly onChange?: (next: Canvas) => void;
  /**
   * Called when the user clicks the "Save" button. Receives the current
   * canvas. The parent is responsible for the HTTP round-trip (writing
   * the optimistic-lock pair, handling 409 responses, etc.).
   */
  readonly onSave?: (canvas: Canvas) => void;
  /**
   * When true, the save button is shown but disabled (e.g. because a
   * save is currently in flight or the canvas has no pending changes).
   */
  readonly saveDisabled?: boolean;
  /**
   * Free-form status text rendered next to the save button. The
   * canvas-editor parent uses this for "Saved ✓", "Saving…", or an
   * error message after a 409.
   */
  readonly statusText?: string;
}

interface NodeShapeProps {
  readonly node: CanvasNode;
  readonly onOpenFile?: CanvasViewerProps['onOpenFile'];
  /** Editor mode flags — all optional; falsy → read-only render. */
  readonly editable?: boolean;
  readonly selected?: boolean;
  readonly onPointerDownNode?: (id: string, evt: ReactMouseEvent) => void;
  readonly onDoubleClickNode?: (id: string) => void;
}

function NodeShape(props: NodeShapeProps): JSX.Element {
  const n = props.node;
  const s = styleForNode(n);
  const fontSize = n.type === 'group' ? 13 : 14;
  const lines = wrapNodeText(
    nodeBody(n),
    n.width,
    fontSize,
    Math.max(1, Math.floor(n.height / 18)),
  );
  const rx = n.type === 'group' ? 4 : 8;

  // In editor mode, a node mousedown starts a drag (handled by the
  // parent component) and click is consumed by the parent's selection
  // logic — `onOpenFile` only fires in read-only mode.
  const onClickViewer =
    !props.editable && n.type === 'file' && props.onOpenFile
      ? () => props.onOpenFile?.(n.file, n.subpath)
      : undefined;
  const onMouseDown = props.editable && props.onPointerDownNode
    ? (e: ReactMouseEvent) => props.onPointerDownNode?.(n.id, e)
    : undefined;
  const onDoubleClick = props.editable && props.onDoubleClickNode
    ? () => props.onDoubleClickNode?.(n.id)
    : undefined;

  const selectionStroke = props.selected ? '#4f8cf7' : s.stroke;
  const selectionStrokeWidth = props.selected ? 2.5 : n.type === 'group' ? 1 : 1.5;

  return (
    <g
      className={`ow-canvas-node ow-canvas-node-${n.type}${props.selected ? ' ow-canvas-node-selected' : ''}`}
      data-node-id={n.id}
      data-testid={`canvas-node-${n.id}`}
      data-selected={props.selected ? 'true' : 'false'}
      onClick={onClickViewer}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      style={onClickViewer || props.editable ? { cursor: props.editable ? 'move' : 'pointer' } : undefined}
    >
      <rect
        x={n.x}
        y={n.y}
        width={n.width}
        height={n.height}
        rx={rx}
        ry={rx}
        fill={s.fill}
        stroke={selectionStroke}
        strokeWidth={selectionStrokeWidth}
      />
      <text
        x={n.x + 12}
        y={n.y}
        fill={s.text}
        fontSize={fontSize}
        fontFamily="var(--font-interface, system-ui, sans-serif)"
      >
        {lines.map((line, i) => (
          <tspan key={i} x={n.x + 12} dy={i === 0 ? fontSize + 4 : fontSize + 2}>
            {line}
          </tspan>
        ))}
      </text>
      {n.type === 'link' ? (
        <title>{n.url}</title>
      ) : n.type === 'file' ? (
        <title>{n.subpath ? `${n.file}${n.subpath}` : n.file}</title>
      ) : null}
    </g>
  );
}

function EdgeShape(props: {
  readonly edge: CanvasEdge;
  readonly from: CanvasNode;
  readonly to: CanvasNode;
  readonly editable?: boolean;
  readonly selected?: boolean;
  readonly onClickEdge?: (id: string, evt: ReactMouseEvent) => void;
}): JSX.Element {
  const { edge, from, to } = props;
  const d = edgePath(from, to, edge.fromSide, edge.toSide);
  const baseStroke = colorToken(edge.color) ?? 'var(--background-modifier-border-hover, #6b7280)';
  const stroke = props.selected ? '#4f8cf7' : baseStroke;
  const toEnd = edge.toEnd ?? 'arrow';
  const fromEnd = edge.fromEnd ?? 'none';
  const a = edgeAnchor(from, edge.fromSide ?? inferSide(from, to));
  const b = edgeAnchor(to, edge.toSide ?? inferSide(to, from));
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const onClick =
    props.editable && props.onClickEdge
      ? (e: ReactMouseEvent) => props.onClickEdge?.(edge.id, e)
      : undefined;
  return (
    <g
      className={`ow-canvas-edge${props.selected ? ' ow-canvas-edge-selected' : ''}`}
      data-edge-id={edge.id}
      data-testid={`canvas-edge-${edge.id}`}
      data-selected={props.selected ? 'true' : 'false'}
      onClick={onClick}
      style={props.editable ? { cursor: 'pointer' } : undefined}
    >
      {props.editable ? (
        // Invisible wide hit-area so edges are clickable without pixel
        // precision.
        <path d={d} fill="none" stroke="transparent" strokeWidth={12} />
      ) : null}
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={props.selected ? 3 : 2}
        {...(toEnd === 'arrow' ? { markerEnd: 'url(#ow-canvas-arrow)' } : {})}
        {...(fromEnd === 'arrow' ? { markerStart: 'url(#ow-canvas-arrow)' } : {})}
      />
      {edge.label ? (
        <text
          x={mx}
          y={my}
          textAnchor="middle"
          fontSize={12}
          fill="var(--text-muted, #a1a1aa)"
          fontFamily="var(--font-interface, system-ui, sans-serif)"
          paintOrder="stroke"
          stroke="var(--background-primary, #1e1e1e)"
          strokeWidth={3}
        >
          {edge.label}
        </text>
      ) : null}
    </g>
  );
}

/* ── Drag state machines (separate for viewport pan vs node move) ────── */

interface PanDragState {
  readonly kind: 'pan';
  readonly startClientX: number;
  readonly startClientY: number;
  readonly startTx: number;
  readonly startTy: number;
}

interface NodeDragState {
  readonly kind: 'node';
  readonly nodeId: string;
  readonly startClientX: number;
  readonly startClientY: number;
  readonly startNodeX: number;
  readonly startNodeY: number;
  /** True once the user has moved past the click-vs-drag threshold. */
  moved: boolean;
}

type DragState = PanDragState | NodeDragState;

const DRAG_THRESHOLD_PX = 3;

export function CanvasViewer(props: CanvasViewerProps): JSX.Element {
  const width = props.width ?? 800;
  const height = props.height ?? 600;
  const editable = props.editable === true;
  const [transform, setTransform] = useState<Transform>(() =>
    fitTransform(props.canvas, width, height),
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  /** Non-null while the user is in "connect" mode: the id of the source
   *  node; the next node click completes the edge (FR-4.3). */
  const [pendingEdgeFrom, setPendingEdgeFrom] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; buffer: string } | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Last canvas value this editor emitted through onChange. Used to
   *  distinguish our own mutations (parent echoes them back as
   *  props.canvas) from a genuinely new document (tab switch / reload):
   *  only the latter should refit the viewport and clear selection. */
  const lastEmittedRef = useRef<Canvas | null>(null);

  const emitChange = useCallback(
    (next: Canvas) => {
      lastEmittedRef.current = next;
      props.onChange?.(next);
    },
    [props],
  );

  // Re-fit when a new canvas (different file) is opened. Selection is
  // cleared on canvas-identity change so a stale id doesn't bleed across
  // documents.
  const canvasIdentity = props.canvas;
  useEffect(() => {
    // Our own mutation coming back through the controlled-component loop —
    // keep viewport, selection, and any in-flight interaction.
    if (lastEmittedRef.current === canvasIdentity) return;
    lastEmittedRef.current = null;
    setTransform(fitTransform(canvasIdentity, width, height));
    setSelected(null);
    setSelectedEdge(null);
    setPendingEdgeFrom(null);
    setEditing(null);
  }, [canvasIdentity, width, height]);

  const screenPoint = useCallback((evt: ReactMouseEvent | ReactWheelEvent): Point => {
    const rect = svgRef.current?.getBoundingClientRect();
    return {
      x: evt.clientX - (rect?.left ?? 0),
      y: evt.clientY - (rect?.top ?? 0),
    };
  }, []);

  /* ── Viewport pan (background drag) ─────────────────────────────── */

  const onMouseDownBackground = useCallback(
    (e: ReactMouseEvent<SVGSVGElement>) => {
      if (e.button !== 0) return;
      // If a node handler claimed this event we leave dragRef alone.
      if (dragRef.current?.kind === 'node') return;
      // Clicking empty space clears selection / pending connect (editor
      // mode only).
      if (editable) {
        setSelected(null);
        setSelectedEdge(null);
        setPendingEdgeFrom(null);
      }
      dragRef.current = {
        kind: 'pan',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startTx: transform.tx,
        startTy: transform.ty,
      };
    },
    [transform, editable],
  );

  /* ── Node drag start (editor mode) ──────────────────────────────── */

  const onPointerDownNode = useCallback(
    (id: string, e: ReactMouseEvent) => {
      if (!editable) return;
      if (e.button !== 0) return;
      // Stop the event so the background mousedown doesn't start a pan.
      e.stopPropagation();
      const node = props.canvas.nodes.find((n) => n.id === id);
      if (!node) return;
      // Connect mode: the second node click completes the edge (FR-4.3).
      if (pendingEdgeFrom !== null) {
        if (pendingEdgeFrom !== id) {
          const { canvas: next, id: edgeId } = addEdge(props.canvas, pendingEdgeFrom, id);
          if (edgeId !== null) {
            emitChange(next);
            setSelectedEdge(edgeId);
            setSelected(null);
          }
        }
        setPendingEdgeFrom(null);
        return;
      }
      setSelected(id);
      setSelectedEdge(null);
      dragRef.current = {
        kind: 'node',
        nodeId: id,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startNodeX: node.x,
        startNodeY: node.y,
        moved: false,
      };
    },
    [editable, props.canvas, pendingEdgeFrom, emitChange],
  );

  const onClickEdge = useCallback(
    (id: string, e: ReactMouseEvent) => {
      if (!editable) return;
      e.stopPropagation();
      setSelectedEdge(id);
      setSelected(null);
      setPendingEdgeFrom(null);
    },
    [editable],
  );

  const onDoubleClickNode = useCallback(
    (id: string) => {
      if (!editable) return;
      const node = props.canvas.nodes.find((n) => n.id === id);
      if (!node) return;
      // Only text and group are inline-editable; link/file open a dialog
      // (rendered below — a simple <input> overlay).
      if (node.type === 'text') {
        setEditing({ id, buffer: node.text });
      } else if (node.type === 'group') {
        setEditing({ id, buffer: node.label ?? '' });
      } else if (node.type === 'file') {
        setEditing({ id, buffer: node.file });
      } else if (node.type === 'link') {
        setEditing({ id, buffer: node.url });
      }
    },
    [editable, props.canvas.nodes],
  );

  /* ── Mouse-move dispatch (pan or node-drag) ─────────────────────── */

  const onMouseMove = useCallback(
    (e: ReactMouseEvent<SVGSVGElement>) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.kind === 'pan') {
        setTransform((prev) => ({
          scale: prev.scale,
          tx: d.startTx + (e.clientX - d.startClientX),
          ty: d.startTy + (e.clientY - d.startClientY),
        }));
      } else {
        // Node drag — convert screen delta → world delta (divide by scale).
        const dxScreen = e.clientX - d.startClientX;
        const dyScreen = e.clientY - d.startClientY;
        if (!d.moved && Math.hypot(dxScreen, dyScreen) >= DRAG_THRESHOLD_PX) {
          d.moved = true;
        }
        if (d.moved && props.onChange) {
          const dxWorld = dxScreen / transform.scale;
          const dyWorld = dyScreen / transform.scale;
          const next = setNodePosition(
            props.canvas,
            d.nodeId,
            d.startNodeX + dxWorld,
            d.startNodeY + dyWorld,
          );
          if (next !== props.canvas) emitChange(next);
        }
      }
    },
    [transform.scale, props, emitChange],
  );

  const endDrag = useCallback(() => {
    dragRef.current = null;
  }, []);

  const onWheel = useCallback(
    (e: ReactWheelEvent<SVGSVGElement>) => {
      e.preventDefault();
      const p = screenPoint(e);
      setTransform((prev) => applyZoom(prev, e.deltaY, p.x, p.y));
    },
    [screenPoint],
  );

  /* ── Keyboard: Delete/Backspace deletes the selection (node or edge) ─ */

  useEffect(() => {
    if (!editable) return;
    const onKey = (e: KeyboardEvent): void => {
      if (editing !== null) return; // typing into the inline editor
      if (selected === null && selectedEdge === null && pendingEdgeFrom === null) return;
      const root = rootRef.current;
      // Only react when the canvas (or its descendants) has focus to
      // avoid stealing keystrokes from the rest of the app.
      if (root && document.activeElement && !root.contains(document.activeElement)) {
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (selected !== null) {
          emitChange(deleteNode(props.canvas, selected));
          setSelected(null);
        } else if (selectedEdge !== null) {
          emitChange(deleteEdge(props.canvas, selectedEdge));
          setSelectedEdge(null);
        }
      } else if (e.key === 'Escape') {
        setSelected(null);
        setSelectedEdge(null);
        setPendingEdgeFrom(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editable, selected, selectedEdge, pendingEdgeFrom, editing, props, emitChange]);

  /* ── Inline edit save/cancel ────────────────────────────────────── */

  const commitEdit = useCallback(() => {
    if (editing === null) return;
    if (props.onChange) {
      const next = updateNodeBody(props.canvas, editing.id, editing.buffer);
      if (next !== props.canvas) emitChange(next);
    }
    setEditing(null);
  }, [editing, props, emitChange]);

  /* ── Toolbar ops: add text node / connect (add edge) ────────────── */

  const onAddTextNode = useCallback(() => {
    // Centre of the current viewport in world space.
    const wx = (width / 2 - transform.tx) / transform.scale;
    const wy = (height / 2 - transform.ty) / transform.scale;
    const { canvas: next, id } = addTextNode(props.canvas, wx, wy);
    emitChange(next);
    setSelected(id);
    setSelectedEdge(null);
    setPendingEdgeFrom(null);
    // Open the inline editor straight away so the placeholder text is
    // immediately replaceable.
    setEditing({ id, buffer: 'New note' });
  }, [width, height, transform, props.canvas, emitChange]);

  const onStartConnect = useCallback(() => {
    if (selected === null) return;
    setPendingEdgeFrom(selected);
    setSelectedEdge(null);
  }, [selected]);

  const cancelEdit = useCallback(() => {
    setEditing(null);
  }, []);

  /* ── Save button ────────────────────────────────────────────────── */

  const onClickSave = useCallback(() => {
    if (props.onSave) props.onSave(props.canvas);
  }, [props]);

  /* ── Render lookup tables ───────────────────────────────────────── */

  const nodeById = useMemo(
    () => new Map(props.canvas.nodes.map((n) => [n.id, n] as const)),
    [props.canvas.nodes],
  );
  const groups = useMemo(
    () => props.canvas.nodes.filter((n): n is GroupNode => n.type === 'group'),
    [props.canvas.nodes],
  );
  const leaves = useMemo(
    () => props.canvas.nodes.filter((n) => n.type !== 'group'),
    [props.canvas.nodes],
  );

  /* ── Inline editor positioning ──────────────────────────────────── */

  const editingNode = editing ? nodeById.get(editing.id) ?? null : null;
  const editorOverlayStyle = (() => {
    if (!editingNode) return null;
    // World-space → screen-space for the textarea overlay.
    const sx = editingNode.x * transform.scale + transform.tx;
    const sy = editingNode.y * transform.scale + transform.ty;
    const sw = editingNode.width * transform.scale;
    const sh = editingNode.height * transform.scale;
    return {
      position: 'absolute' as const,
      left: sx,
      top: sy,
      width: sw,
      height: sh,
      zIndex: 2,
    };
  })();

  return (
    <div
      ref={rootRef}
      tabIndex={editable ? 0 : -1}
      className={`ow-canvas-viewer${editable ? ' ow-canvas-editor' : ''}`}
      style={{ width, height, position: 'relative', overflow: 'hidden', outline: 'none' }}
      data-testid="canvas-viewer-root"
      data-editable={editable ? 'true' : 'false'}
    >
      {editable ? (
        <div
          className="ow-canvas-toolbar"
          data-testid="canvas-toolbar"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 3,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            background: 'var(--background-primary, rgba(20,20,20,0.85))',
            border: '1px solid var(--background-modifier-border, #3f3f46)',
            borderRadius: 6,
            padding: '4px 8px',
            fontSize: 12,
            color: 'var(--text-normal, #dcddde)',
            fontFamily: 'var(--font-interface, system-ui, sans-serif)',
          }}
        >
          {props.statusText ? (
            <span data-testid="canvas-status" style={{ color: 'var(--text-muted, #a1a1aa)' }}>
              {props.statusText}
            </span>
          ) : null}
          {pendingEdgeFrom !== null ? (
            <span
              data-testid="canvas-connect-hint"
              style={{ color: 'var(--text-accent, #7f6df2)' }}
            >
              Click a target node…
            </span>
          ) : null}
          <button
            type="button"
            data-testid="canvas-add-text-button"
            onClick={onAddTextNode}
            title="Add a text card at the centre of the view"
            style={{
              border: '1px solid var(--background-modifier-border, #3f3f46)',
              background: 'transparent',
              color: 'var(--text-normal, #dcddde)',
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            + Text
          </button>
          <button
            type="button"
            data-testid="canvas-connect-button"
            onClick={onStartConnect}
            disabled={selected === null}
            title={
              selected === null
                ? 'Select a node first, then connect it to another node'
                : 'Connect the selected node to another node'
            }
            style={{
              border: '1px solid var(--background-modifier-border, #3f3f46)',
              background: pendingEdgeFrom !== null ? 'var(--interactive-accent, #4f8cf7)' : 'transparent',
              color:
                selected === null && pendingEdgeFrom === null
                  ? 'var(--text-muted, #a1a1aa)'
                  : pendingEdgeFrom !== null
                    ? '#fff'
                    : 'var(--text-normal, #dcddde)',
              borderRadius: 4,
              padding: '4px 10px',
              cursor: selected === null ? 'not-allowed' : 'pointer',
              fontSize: 12,
            }}
          >
            Connect
          </button>
          <button
            type="button"
            data-testid="canvas-save-button"
            onClick={onClickSave}
            disabled={props.saveDisabled === true || !props.onSave}
            style={{
              border: '1px solid var(--background-modifier-border, #3f3f46)',
              background:
                props.saveDisabled === true || !props.onSave
                  ? 'transparent'
                  : 'var(--interactive-accent, #4f8cf7)',
              color:
                props.saveDisabled === true || !props.onSave
                  ? 'var(--text-muted, #a1a1aa)'
                  : '#fff',
              borderRadius: 4,
              padding: '4px 10px',
              cursor:
                props.saveDisabled === true || !props.onSave ? 'not-allowed' : 'pointer',
              fontSize: 12,
            }}
          >
            Save
          </button>
        </div>
      ) : null}
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="ow-canvas-svg"
        role="img"
        aria-label="Canvas diagram"
        onMouseDown={onMouseDownBackground}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onWheel={onWheel}
        style={{
          cursor: dragRef.current ? 'grabbing' : 'grab',
          background: 'var(--background-secondary, #181818)',
          userSelect: 'none',
        }}
      >
        <defs>
          <marker
            id="ow-canvas-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path
              d="M0,0 L10,5 L0,10 z"
              fill="var(--background-modifier-border-hover, #6b7280)"
            />
          </marker>
        </defs>
        <g
          transform={`translate(${transform.tx} ${transform.ty}) scale(${transform.scale})`}
          data-testid="canvas-viewport"
        >
          {groups.map((g) => (
            <NodeShape
              key={g.id}
              node={g}
              editable={editable}
              selected={selected === g.id}
              {...(editable ? { onPointerDownNode } : {})}
              {...(editable ? { onDoubleClickNode } : {})}
            />
          ))}
          {props.canvas.edges.map((edge) => {
            const from = nodeById.get(edge.fromNode);
            const to = nodeById.get(edge.toNode);
            if (!from || !to) return null;
            return (
              <EdgeShape
                key={edge.id}
                edge={edge}
                from={from}
                to={to}
                editable={editable}
                selected={selectedEdge === edge.id}
                {...(editable ? { onClickEdge } : {})}
              />
            );
          })}
          {leaves.map((n) => (
            <NodeShape
              key={n.id}
              node={n}
              editable={editable}
              selected={selected === n.id}
              {...(props.onOpenFile && !editable ? { onOpenFile: props.onOpenFile } : {})}
              {...(editable ? { onPointerDownNode } : {})}
              {...(editable ? { onDoubleClickNode } : {})}
            />
          ))}
        </g>
      </svg>
      {editing && editorOverlayStyle ? (
        <textarea
          data-testid="canvas-node-editor"
          autoFocus
          value={editing.buffer}
          onChange={(e) => setEditing({ id: editing.id, buffer: e.target.value })}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              commitEdit();
            }
          }}
          style={{
            ...editorOverlayStyle,
            padding: 8,
            font: 'inherit',
            background: 'var(--background-primary, #1e1e1e)',
            color: 'var(--text-normal, #dcddde)',
            border: '2px solid #4f8cf7',
            borderRadius: 8,
            resize: 'none',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      ) : null}
    </div>
  );
}

export default CanvasViewer;
